export type AgentProgressKind = 'thinking' | 'tool' | 'assistant' | 'status';

export type AgentProgressStep = {
  readonly kind: AgentProgressKind;
  readonly text: string;
};

export type AgentStreamEvent = {
  readonly type: string;
  readonly text?: string;
  readonly name?: string;
  readonly status?: string;
  readonly message?: unknown;
};

export type AssistantContentBlock = {
  readonly type?: string;
  readonly text?: string;
};

export type AssistantMessagePayload = {
  readonly content?: unknown;
};

const MAX_PROGRESS_STEPS = 40;

export function progressStepFromStreamEvent(
  event: AgentStreamEvent
): AgentProgressStep | undefined {
  if (event.type === 'thinking') {
    return optionalStep('thinking', event.text);
  }
  if (event.type === 'tool_call') {
    return undefined;
  }
  if (event.type === 'assistant') {
    return optionalStep('assistant', assistantTextFromEvent(event));
  }
  if (event.type === 'status') {
    return optionalStep('status', statusText(event));
  }
  if (event.type === 'task') {
    return optionalStep('status', event.text ?? event.status);
  }
  return undefined;
}

export function isTerminalStreamStatus(status: string | undefined): boolean {
  if (status === undefined) {
    return false;
  }
  const normalized = status.toUpperCase();
  return (
    normalized === 'FINISHED' ||
    normalized === 'ERROR' ||
    normalized === 'CANCELLED' ||
    normalized === 'EXPIRED'
  );
}

export function lastAssistantText(progress: readonly AgentProgressStep[]): string | undefined {
  const assistants = progress.filter((step) => step.kind === 'assistant');
  const last = assistants[assistants.length - 1];
  if (last === undefined || last.text.trim() === '') {
    return undefined;
  }
  return last.text;
}

export function thinkingTrace(
  progress: readonly AgentProgressStep[]
): readonly AgentProgressStep[] {
  return progress.filter((step) => step.kind === 'thinking' || step.kind === 'status');
}

export function isTerminalProgressStep(step: AgentProgressStep): boolean {
  return step.kind === 'status' && isTerminalStreamStatus(step.text);
}

export function appendProgressStep(
  steps: readonly AgentProgressStep[],
  next: AgentProgressStep
): readonly AgentProgressStep[] {
  const last = steps[steps.length - 1];
  if (last !== undefined && last.kind === next.kind && isMergeableKind(next.kind)) {
    return [
      ...steps.slice(0, -1),
      { kind: next.kind, text: mergeStreamedText(last.text, next.text) },
    ];
  }
  const appended = [...steps, next];
  if (appended.length <= MAX_PROGRESS_STEPS) {
    return appended;
  }
  return appended.slice(appended.length - MAX_PROGRESS_STEPS);
}

export function mergeStreamedText(previous: string, next: string): string {
  if (next.startsWith(previous)) {
    return next;
  }
  return `${previous}${next}`;
}

function isMergeableKind(kind: AgentProgressKind): boolean {
  return kind === 'thinking' || kind === 'assistant';
}

function optionalStep(
  kind: AgentProgressKind,
  text: string | undefined
): AgentProgressStep | undefined {
  if (text === undefined) {
    return undefined;
  }
  if (kind === 'thinking' || kind === 'assistant') {
    if (text === '') {
      return undefined;
    }
    return { kind, text };
  }
  const trimmed = text.trim();
  if (trimmed === '') {
    return undefined;
  }
  return { kind, text: trimmed };
}

function statusText(event: AgentStreamEvent): string | undefined {
  if (typeof event.message === 'string' && event.message.trim() !== '') {
    return event.message;
  }
  return event.status;
}

function assistantTextFromEvent(event: AgentStreamEvent): string | undefined {
  if (event.message === null || typeof event.message !== 'object') {
    return undefined;
  }
  const { content } = event.message as AssistantMessagePayload;
  if (!Array.isArray(content)) {
    return undefined;
  }
  const texts = content
    .filter(isTextBlock)
    .map((block) => block.text)
    .filter((text): text is string => text !== undefined && text.trim() !== '');
  if (texts.length === 0) {
    return undefined;
  }
  return texts.join('');
}

function isTextBlock(block: unknown): block is AssistantContentBlock {
  if (block === null || typeof block !== 'object') {
    return false;
  }
  if (!('type' in block)) {
    return false;
  }
  return block.type === 'text';
}
